// ─────────────────────────────────────────────────────────────
//  Settings → Setup-Pakete
//
//  Editor für wiederverwendbare Vorlagen von Einmalzahlungen: Name, Preis,
//  Beschreibungstext und kurze Feature-Stichpunkte. In der Sales Pipeline
//  stehen die Pakete am Kunden als Schnellauswahl bereit und füllen das
//  Formular für die Einmalzahlung mit einem Klick.
//
//  Nichts hier erzeugt eine Rechnung – das passiert erst, wenn ein Paket am
//  Kunden angelegt wird. Diese Seite ist reiner Katalog.
// ─────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { staggerContainer, itemVariants } from './dashboardMotion';
import { database } from '../firebaseConfig';
import { ref, onValue, update, remove, serverTimestamp } from 'firebase/database';
import {
  SETUP_PACKAGES_PATH,
  MAX_FEATURES,
  MAX_LABEL,
  MAX_FEATURE,
  MAX_DESCRIPTION,
  packageList,
  newPackageId,
} from './setupPackagesCore';
import './SetupPackages.css';

const fmtEur = (n) =>
  !isFinite(n) ? '–' : new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(Math.round(n)) + ' €';

const parseNum = (v) => {
  const n = parseFloat(String(v).replace(/\./g, '').replace(',', '.').replace(/[^0-9.-]/g, ''));
  return isFinite(n) ? n : 0;
};

const emptyDraft = () => ({ id: null, label: '', amount: '', description: '', features: [''], active: true });

const toDraft = (p) => ({
  id: p.id,
  label: p.label,
  amount: p.amount > 0 ? String(p.amount) : '',
  description: p.description,
  features: p.features.length ? [...p.features] : [''],
  active: p.active,
});

const SetupPackages = () => {
  const [raw, setRaw] = useState({});
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const off = onValue(ref(database, SETUP_PACKAGES_PATH), (snap) => {
      setRaw(snap.val() || {});
      setLoading(false);
    });
    return () => off();
  }, []);

  const packages = useMemo(() => packageList(raw), [raw]);
  const activeCount = packages.filter((p) => p.active).length;

  const setField = (field, value) => setDraft((d) => ({ ...d, [field]: value }));
  const setFeature = (i, value) => setDraft((d) => {
    const features = [...d.features];
    features[i] = value;
    return { ...d, features };
  });
  const addFeature = () => setDraft((d) => (
    d.features.length >= MAX_FEATURES ? d : { ...d, features: [...d.features, ''] }
  ));
  const removeFeature = (i) => setDraft((d) => {
    const features = d.features.filter((_, idx) => idx !== i);
    return { ...d, features: features.length ? features : [''] };
  });

  const cleanFeatures = (list) =>
    list.map((f) => f.trim()).filter(Boolean).slice(0, MAX_FEATURES).map((f) => f.slice(0, MAX_FEATURE));

  const amount = draft ? Math.max(0, Math.round(parseNum(draft.amount))) : 0;
  const canSave = !!(draft && draft.label.trim() && amount > 0);

  const save = async () => {
    if (!canSave) return;
    const id = draft.id || newPackageId();
    const prev = draft.id ? raw[draft.id] : null;
    setError('');
    try {
      await update(ref(database, `${SETUP_PACKAGES_PATH}/${id}`), {
        label: draft.label.trim().slice(0, MAX_LABEL),
        amount,
        description: draft.description.trim().slice(0, MAX_DESCRIPTION) || null,
        // Als Ganzes ersetzen: gelöschte Stichpunkte müssen verschwinden.
        features: cleanFeatures(draft.features).length ? cleanFeatures(draft.features) : null,
        active: draft.active !== false,
        createdAt: (prev && Number(prev.createdAt)) || Date.now(),
        updatedAt: serverTimestamp(),
      });
      setDraft(null);
    } catch (e) {
      setError(e.message || 'Speichern fehlgeschlagen.');
    }
  };

  const toggleActive = (p) =>
    update(ref(database, `${SETUP_PACKAGES_PATH}/${p.id}`), { active: !p.active, updatedAt: serverTimestamp() })
      .catch((e) => setError(e.message || 'Aktualisieren fehlgeschlagen.'));

  const duplicate = (p) => setDraft({
    id: null,
    label: `${p.label} (Kopie)`,
    amount: p.amount > 0 ? String(p.amount) : '',
    description: p.description,
    features: p.features.length ? [...p.features] : [''],
    active: true,
  });

  const removePackage = (p) => {
    if (!window.confirm(`Paket „${p.label}" wirklich löschen?\n\nBereits am Kunden angelegte Einmalzahlungen bleiben unverändert – sie haben eine eigene Kopie von Preis und Text.`)) return;
    remove(ref(database, `${SETUP_PACKAGES_PATH}/${p.id}`))
      .catch((e) => setError(e.message || 'Löschen fehlgeschlagen.'));
  };

  return (
    <motion.div className="sp-wrap" variants={staggerContainer} initial="initial" animate="animate">
      <motion.div className="sp-head" variants={itemVariants}>
        <div className="sp-head-text">
          <h2>Setup-Pakete</h2>
          <p>
            Vorlagen für alles, was einmalig abgerechnet wird – Setup, Beratung, Workshop. Beim Kunden
            stehen sie unter <strong>Deal &amp; Team → Einmalzahlungen</strong> als Schnellauswahl bereit und füllen
            Preis, Text und Features mit einem Klick. Provisionen bleiben bewusst Sache des einzelnen Kunden.
          </p>
        </div>
        {!draft && (
          <button type="button" className="sp-btn sp-btn-primary" onClick={() => setDraft(emptyDraft())}>
            + Neues Paket
          </button>
        )}
      </motion.div>

      {error && <motion.div className="sp-error" variants={itemVariants}>{error}</motion.div>}

      {draft && (
        <motion.div className="sp-form" variants={itemVariants}>
          <div className="sp-form-row">
            <label className="sp-field sp-field-wide">
              <span>Name des Pakets</span>
              <input
                type="text"
                value={draft.label}
                maxLength={MAX_LABEL}
                placeholder="z. B. Setup & Onboarding"
                onChange={(e) => setField('label', e.target.value)}
              />
            </label>
            <label className="sp-field sp-field-price">
              <span>Preis netto (€)</span>
              <input
                type="text"
                inputMode="numeric"
                value={draft.amount}
                placeholder="0"
                onChange={(e) => setField('amount', e.target.value)}
              />
            </label>
          </div>

          <label className="sp-field">
            <span>Beschreibung</span>
            <textarea
              rows={3}
              value={draft.description}
              maxLength={MAX_DESCRIPTION}
              placeholder="Ein bis zwei Sätze, die erklären, was der Kunde bekommt."
              onChange={(e) => setField('description', e.target.value)}
            />
            <em className="sp-count">{draft.description.length} / {MAX_DESCRIPTION}</em>
          </label>

          <div className="sp-features">
            <div className="sp-features-head">
              <span>Features</span>
              <span className="sp-hint">Kurze Stichpunkte – was ist enthalten?</span>
            </div>
            {draft.features.map((f, i) => (
              <div key={i} className="sp-feature-row">
                <span className="sp-bullet" aria-hidden="true">•</span>
                <input
                  type="text"
                  value={f}
                  maxLength={MAX_FEATURE}
                  placeholder={i === 0 ? 'z. B. Technisches Setup der Mailboxen' : 'Weiterer Punkt …'}
                  onChange={(e) => setFeature(i, e.target.value)}
                />
                <button
                  type="button"
                  className="sp-btn sp-btn-ghost sp-btn-icon"
                  aria-label="Punkt entfernen"
                  onClick={() => removeFeature(i)}
                >
                  ✕
                </button>
              </div>
            ))}
            {draft.features.length < MAX_FEATURES && (
              <button type="button" className="sp-btn sp-btn-ghost sp-btn-add" onClick={addFeature}>
                + Punkt hinzufügen
              </button>
            )}
          </div>

          <label className="sp-toggle">
            <input type="checkbox" checked={draft.active !== false} onChange={(e) => setField('active', e.target.checked)} />
            <span>In der Schnellauswahl anzeigen</span>
          </label>

          <div className="sp-form-actions">
            <button type="button" className="sp-btn sp-btn-ghost" onClick={() => { setDraft(null); setError(''); }}>Abbrechen</button>
            <button type="button" className="sp-btn sp-btn-primary" disabled={!canSave} onClick={save}>
              {draft.id ? 'Änderungen speichern' : 'Paket anlegen'}
            </button>
          </div>
        </motion.div>
      )}

      {loading ? (
        <div className="sp-empty">Pakete werden geladen …</div>
      ) : packages.length === 0 ? (
        !draft && (
          <div className="sp-empty">
            Noch keine Pakete angelegt. Leg dein erstes an – danach kannst du es bei jedem Kunden
            mit einem Klick als Einmalzahlung übernehmen.
          </div>
        )
      ) : (
        <>
          <motion.div className="sp-count-line" variants={itemVariants}>
            {packages.length} Paket{packages.length === 1 ? '' : 'e'}
            {activeCount !== packages.length && ` · ${activeCount} in der Schnellauswahl`}
          </motion.div>
          <motion.div className="sp-grid" variants={staggerContainer}>
            {packages.map((p) => (
              <motion.div key={p.id} className={`sp-card ${p.active ? '' : 'is-off'}`} variants={itemVariants}>
                <div className="sp-card-head">
                  <span className="sp-card-title">{p.label}</span>
                  <span className="sp-card-price">{fmtEur(p.amount)}<em>netto</em></span>
                </div>
                {p.description && <p className="sp-card-desc">{p.description}</p>}
                {!!p.features.length && (
                  <ul className="sp-card-features">
                    {p.features.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                )}
                <div className="sp-card-actions">
                  {!p.active && <span className="sp-tag">ausgeblendet</span>}
                  <button type="button" className="sp-btn sp-btn-ghost" onClick={() => setDraft(toDraft(p))}>Bearbeiten</button>
                  <button type="button" className="sp-btn sp-btn-ghost" onClick={() => duplicate(p)}>Duplizieren</button>
                  <button type="button" className="sp-btn sp-btn-ghost" onClick={() => toggleActive(p)}>
                    {p.active ? 'Ausblenden' : 'Einblenden'}
                  </button>
                  <button type="button" className="sp-btn sp-btn-ghost sp-btn-del" onClick={() => removePackage(p)} title="Paket löschen">✕</button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </>
      )}
    </motion.div>
  );
};

export default SetupPackages;
